/**
 * How a ticketed event's date, time, and price read to the public (Event
 * Tickets plan 2026-10-19, U4, U6). Pure, so the event page, the catalog
 * card, the order page, and the email print the same words.
 *
 * Times are shown on the event's own clock (its time zone), with the zone
 * name, so a buyer in another state reads the venue's time.
 */

import { formatCurrencyFromCents } from '@/utils/formatters/currencyUtils';
import { DEFAULT_SERVER_TIMEZONE } from '@/utils/timezone/serverLocalDate';

/** Cents as "$1,234.50" (the shared currency formatter). */
export const formatTicketMoney = formatCurrencyFromCents;

function parts(iso: string, timeZone: string, options: Intl.DateTimeFormatOptions): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone, ...options }).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat('en-US', { timeZone: DEFAULT_SERVER_TIMEZONE, ...options }).format(new Date(iso));
  }
}

/** "Sat, Dec 13, 2026". */
export function ticketEventDate(iso: string, timeZone: string): string {
  return parts(iso, timeZone, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

/** "2:00 PM". */
export function ticketEventTime(iso: string, timeZone: string): string {
  return parts(iso, timeZone, { hour: 'numeric', minute: '2-digit' });
}

/** "EST". */
function zoneName(iso: string, timeZone: string): string {
  const all = parts(iso, timeZone, { hour: 'numeric', timeZoneName: 'short' });
  const m = all.match(/[A-Z]{2,5}$|GMT[+-]\d+$/);
  return m ? m[0] : '';
}

/**
 * "Sat, Dec 13, 2026 · 2:00 PM – 5:00 PM EST", "All day", or two dates for
 * an event that ends on a later day.
 */
export function ticketEventWhen(e: { start_time: string; end_time: string; is_all_day: boolean; time_zone: string }): string {
  const startDate = ticketEventDate(e.start_time, e.time_zone);
  const endDate = ticketEventDate(e.end_time, e.time_zone);
  if (e.is_all_day) return startDate === endDate ? `${startDate} · All day` : `${startDate} – ${endDate}`;
  const zone = zoneName(e.start_time, e.time_zone);
  const start = ticketEventTime(e.start_time, e.time_zone);
  const end = ticketEventTime(e.end_time, e.time_zone);
  if (startDate === endDate) return `${startDate} · ${start} – ${end}${zone ? ` ${zone}` : ''}`;
  return `${startDate} ${start} – ${endDate} ${end}${zone ? ` ${zone}` : ''}`;
}

/** "Mon, Dec 8 at 9:00 PM EST", for the "sales close" line. */
export function ticketMoment(iso: string, timeZone: string): string {
  const zone = zoneName(iso, timeZone);
  return `${parts(iso, timeZone, { weekday: 'short', month: 'short', day: 'numeric' })} at ${ticketEventTime(iso, timeZone)}${zone ? ` ${zone}` : ''}`;
}

/** "$12.60" or "$6.30 – $12.60": the all-in price range of the public types. */
export function ticketPriceRange(min: number | null, max: number | null): string | null {
  if (min == null || max == null) return null;
  return min === max ? formatTicketMoney(min) : `${formatTicketMoney(min)} – ${formatTicketMoney(max)}`;
}

/** "Riverside Center, 12 Main St, Leesburg, VA", whichever parts exist. */
export function ticketVenueLine(e: { venue_name: string | null; venue_address: string | null }): string | null {
  const name = (e.venue_name || '').trim();
  const address = (e.venue_address || '').trim();
  if (name && address && !address.toLowerCase().startsWith(name.toLowerCase())) return `${name}, ${address}`;
  return name || address || null;
}

/** "2 Adult, 1 Child". */
export function ticketCountsLine(lines: Array<{ name: string; quantity: number }>): string {
  return lines.map((l) => `${l.quantity} ${l.name}`).join(', ');
}
